/**
 * @fileoverview Shared Grid Templates manager for every admin DataGrid.
 * @module ui-admin/GridPageShell/GridTemplatesModal
 *
 * Responsibilities:
 * - Save, apply, update, share, and delete named grid layouts.
 * - Show each template's column count and confirm before overwriting.
 *
 * Dependencies:
 * - DataGrid persistence helpers, FeatureTierContext, ConfirmActionModal,
 *   ConfirmDeleteModal, loadTemplates/saveTemplates.
 *
 * Architecture:
 * - Presentation-only. Hosts inject gridId, currentState, and onApply.
 *   Persistence is browser localStorage (not Dexie).
 *
 * Notes:
 * - Apply is the only layout switch. Star bookmarks a default and does not apply.
 * - Save snapshots live colvis/colorder. Update confirms then overwrites that name.
 * - Locations injects extraAutosaveKeys + fallbackColumnVisibility for sa_* columns.
 */
import React, { useState } from 'react';
import { X, LayoutGrid, Save, Share2, Star, Trash2 } from 'lucide-react';
import {
  clearPersistedGridLayout,
  snapshotLiveGridLayout,
  cloneGridState,
  resolveFactoryGridState,
  resolveTemplateColumnVisibility,
  formatTemplateColumnSummary,
  writeAppliedTemplate,
  writePendingDefault,
} from '../DataGrid';
import type { GridState, GridTemplate } from '../DataGrid';
import { loadTemplates, saveTemplates, generateTemplateId } from './useGridPage';
import ConfirmDeleteModal from '../ui/ConfirmDeleteModal';
import ConfirmActionModal from '../ui/ConfirmActionModal';
import { useFeatureTier } from '../../contexts/FeatureTierContext';

/**
 * @contract GridTemplatesModalProps
 * Purpose: Host-injected wiring for the shared Grid Templates manager.
 * Fields:  gridId, gridType, seedTemplates, currentState, onApply, onClose, currentUser
 * Rules:   onApply receives a cloned snapshot plus `{ id, name }` on Apply, or
 *          `null` on Reset so parents clear the applied-template badge together
 *          with localStorage. extraAutosaveKeys/fallbackColumnVisibility
 *          are optional Location-only injects for sa_* hidden columns.
 * Sync:    localStorage only; not replicated.
 */
export interface GridTemplatesModalProps {
  gridId: string;
  gridType: string;
  seedTemplates: GridTemplate[];
  currentState: GridState;
  onApply: (state: GridState, meta?: { id: string; name: string } | null) => void;
  onClose: () => void;
  currentUser: { userId: string; displayName: string };
  defaultPageSize?: number;
  defaultSorting?: { id: string; desc: boolean }[];
  defaultGroupBy?: string[];
  isAdmin?: boolean;
  tier?: number;
  canCreateTemplates?: boolean;
  knownColumnIds?: string[];
  extraAutosaveKeys?: string[];
  fallbackColumnVisibility?: Record<string, boolean>;
}

/**
 * @component GridTemplatesModal
 * @reusable
 * Purpose:     Named layout manager for one DataGrid.
 * Usage:       GridPageShell and host pages (Locations, Inspections, Lookups).
 * Rules:       presentation-only; hosts inject persistence via onApply and seedTemplates.
 * Props:       gridId, gridType, seedTemplates, currentState, onApply, onClose, currentUser
 * Behavior:    Save/Update persist to localStorage; Apply calls onApply then closes.
 * Data Source: local (localStorage templates + live colvis/colorder)
 * Constraints: presentation-only; no Dexie, no API
 * Performance: template list is small; no virtualization
 */
export default function GridTemplatesModal({
  gridId,
  gridType,
  seedTemplates,
  currentState,
  onApply,
  onClose,
  currentUser,
  defaultPageSize = 25,
  defaultSorting,
  defaultGroupBy,
  isAdmin: isAdminProp,
  tier: tierProp,
  canCreateTemplates: canCreateTemplatesProp,
  knownColumnIds = [],
  extraAutosaveKeys = [],
  fallbackColumnVisibility,
}: GridTemplatesModalProps) {
  const featureTier = useFeatureTier();
  const isAdmin = isAdminProp ?? featureTier.isAdmin;
  const effectiveTier = tierProp ?? featureTier.tier;
  // Explicit host override wins; otherwise preserve the pre-shared-modal policy:
  // only admins or tier-3+ tenants may save/update/delete personal layouts.
  const canCreateTemplates = canCreateTemplatesProp ?? (isAdmin || effectiveTier >= 3);
  // Shared layouts were previously behind a Locations/Inspections tier-2 gate.
  // Keep that boundary in the shared modal; system admins remain an exception.
  const canUseSharedTemplates = isAdmin || effectiveTier >= 2;
  const [templates, setTemplates] = useState<GridTemplate[]>(() => loadTemplates(gridId, seedTemplates));
  const [saveName, setSaveName] = useState('');

  const systemTemplates = templates.filter((t) => t.scope === 'system' && !t.isArchived);
  const sharedTemplates = templates.filter((t) => t.scope === 'shared' && !t.isArchived);
  const myTemplates = templates.filter((t) => t.scope === 'personal' && !t.isArchived && t.createdBy.userId === currentUser.userId);

  const persist = (updated: GridTemplate[]) => { setTemplates(updated); saveTemplates(gridId, updated); };

  const liveKnownIds = knownColumnIds.length > 0
    ? knownColumnIds
    : Object.keys(currentState.columnVisibility ?? {});

  const liveLayout = () => snapshotLiveGridLayout(gridId, currentState, liveKnownIds);

  const handleSave = (scope: 'personal' | 'shared') => {
    if (scope === 'shared' && !canUseSharedTemplates) return;
    const trimmed = saveName.trim();
    if (!trimmed) return;
    const now = new Date().toISOString();
    const existing = templates.find((t) => (
      !t.isArchived
      && t.scope === scope
      && t.name.trim().toLowerCase() === trimmed.toLowerCase()
      && (scope === 'shared' || t.createdBy.userId === currentUser.userId)
    ));
    if (existing && existing.scope !== 'system') {
      persist(templates.map((t) => t._id !== existing._id ? t : {
        ...t,
        state: liveLayout(),
        updatedBy: currentUser,
        updatedAt: now,
      }));
      setSaveName('');
      return;
    }
    persist([...templates, {
      _id: generateTemplateId(), gridType, name: trimmed, state: liveLayout(), scope,
      createdBy: currentUser, isDefault: false, isPinned: false, usageCount: 0, createdAt: now, updatedAt: now,
    }]);
    setSaveName('');
  };

  const [updateTarget, setUpdateTarget] = useState<GridTemplate | null>(null);
  const [updateNotice, setUpdateNotice] = useState<string | null>(null);
  const handleUpdate = (id: string) => {
    const tpl = templates.find((t) => t._id === id);
    if (tpl && tpl.scope !== 'system') setUpdateTarget(tpl);
  };
  const confirmUpdate = () => {
    if (!updateTarget || updateTarget.scope === 'system') return;
    if (updateTarget.scope === 'shared' && !canUseSharedTemplates) return;
    const now = new Date().toISOString();
    persist(templates.map((t) => t._id !== updateTarget._id ? t : {
      ...t,
      state: liveLayout(),
      updatedBy: currentUser,
      updatedAt: now,
    }));
    setUpdateNotice(`${updateTarget.name} was updated with the columns currently showing.`);
    setUpdateTarget(null);
  };

  const [deleteTarget, setDeleteTarget] = useState<GridTemplate | null>(null);
  const handleDelete = (id: string) => {
    const tpl = templates.find((t) => t._id === id);
    if (tpl) setDeleteTarget(tpl);
  };
  const confirmDelete = () => {
    if (!deleteTarget) return;
    if (deleteTarget.scope === 'shared' && !canUseSharedTemplates) return;
    persist(templates.filter((t) => t._id !== deleteTarget._id));
    setDeleteTarget(null);
  };
  const handleToggleShare = (id: string) => {
    if (!canUseSharedTemplates) return;
    const now = new Date().toISOString();
    persist(templates.map((t) => t._id !== id ? t : { ...t, scope: t.scope === 'shared' ? 'personal' as const : 'shared' as const, updatedBy: currentUser, updatedAt: now }));
  };
  const handleApply = (tpl: GridTemplate) => {
    if (tpl.scope === 'shared' && !canUseSharedTemplates) return;
    const snapshot = cloneGridState(tpl.state);
    const stored = snapshot.columnVisibility ?? {};
    const hasNamedDataColumns = Object.keys(stored).some((id) => (
      !id.startsWith('sa_') && id !== '_actions' && id !== '_select'
    ));
    if (fallbackColumnVisibility || hasNamedDataColumns) {
      snapshot.columnVisibility = resolveTemplateColumnVisibility(
        stored,
        liveKnownIds,
        fallbackColumnVisibility,
      );
    }
    writePendingDefault(gridId, null);
    onApply(snapshot, { id: tpl._id, name: tpl.name });
    onClose();
  };
  const handleSetDefault = (id: string) => {
    const next = templates.map((t) => ({ ...t, isDefault: t._id === id ? !t.isDefault : false }));
    persist(next);
    const starred = next.find((t) => t._id === id && t.isDefault);
    writePendingDefault(gridId, starred ? { id: starred._id, name: starred.name } : null);
  };
  const handleResetDefaults = () => {
    clearPersistedGridLayout(gridId, extraAutosaveKeys);
    writeAppliedTemplate(gridId, null);
    writePendingDefault(gridId, null);
    onApply(resolveFactoryGridState({
      defaultPageSize,
      defaultSorting,
      defaultGroupBy,
      fallbackColumnVisibility,
    }), null);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-100 flex items-start justify-center pt-24" onClick={onClose}>
      <div className="absolute inset-0 bg-black/20" />
      <div className="relative bg-white rounded-xl shadow-2xl border border-gray-200 w-[480px] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2.5">
            <LayoutGrid className="h-5 w-5 text-gray-500" />
            <div>
              <h2 className="text-base font-semibold text-gray-900">Grid Templates</h2>
              <p className="text-xs text-gray-500">Logged in as {isAdmin ? 'System Admin' : 'Current User'}{isAdmin && <span className="text-orange-600 font-medium"> (Admin)</span>}</p>
            </div>
          </div>
          <button onClick={onClose} title="Close" className="p-1 text-gray-400 hover:text-gray-600 rounded-sm"><X className="h-4 w-4" /></button>
        </div>

        {updateNotice && (
          <div className="px-5 py-2 border-b border-green-100 bg-green-50 flex items-start gap-2">
            <p className="flex-1 text-xs text-green-800">{updateNotice}</p>
            <button type="button" onClick={() => setUpdateNotice(null)} className="p-0.5 text-green-700 hover:text-green-900" title="Dismiss">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        )}

        {canCreateTemplates && (
          <div className="px-5 py-3 border-b border-gray-100 bg-gray-50/50">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-1">Save Current Layout</p>
            <p className="text-[10px] text-gray-400 mb-2">Saves the columns you currently have showing. Update asks you to confirm before replacing that named layout.</p>
            <div className="flex items-center gap-2">
              <input type="text" value={saveName} onChange={(e) => setSaveName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSave('personal'); }}
                placeholder="Template name"
                className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500" />
              <button onClick={() => handleSave('personal')} disabled={!saveName.trim()}
                className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-orange-600 text-white text-sm font-medium rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                <Save className="h-3.5 w-3.5" /> Save
              </button>
              {isAdmin && (
                <button onClick={() => handleSave('shared')} disabled={!saveName.trim()}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 text-gray-600 hover:bg-orange-50 hover:border-orange-300 hover:text-orange-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                  <Share2 className="h-3.5 w-3.5" /> Shared
                </button>
              )}
            </div>
          </div>
        )}

        <div className="max-h-[380px] overflow-auto">
          <TemplateSection label="System Templates" templates={systemTemplates} isAdmin={isAdmin} currentUserId=""
            onApply={handleApply} onDelete={handleDelete} onToggleShare={handleToggleShare} onSetDefault={handleSetDefault} onUpdate={handleUpdate} knownColumnIds={liveKnownIds} />
          {canUseSharedTemplates && (
            <TemplateSection label="Shared Templates" templates={sharedTemplates} isAdmin={isAdmin} currentUserId={currentUser.userId}
              onApply={handleApply} onDelete={handleDelete} onToggleShare={handleToggleShare} onSetDefault={handleSetDefault} onUpdate={handleUpdate} knownColumnIds={liveKnownIds} />
          )}
          {canCreateTemplates && (
            <TemplateSection label="My Templates" templates={myTemplates} isAdmin={isAdmin} currentUserId={currentUser.userId}
              onApply={handleApply} onDelete={handleDelete} onToggleShare={handleToggleShare} onSetDefault={handleSetDefault} onUpdate={handleUpdate} forceOwner canCreateTemplates knownColumnIds={liveKnownIds} />
          )}
        </div>

        <div className="flex items-center justify-between px-5 py-3 border-t border-gray-200 bg-gray-50/50 rounded-b-xl">
          <button onClick={handleResetDefaults} className="text-xs text-gray-500 hover:text-orange-600 transition-colors">Reset to defaults</button>
          <span className="text-[10px] text-gray-400">Grid auto-saves when you leave</span>
        </div>
      </div>

      {updateTarget && (
        <div className="fixed inset-0 z-110" onClick={(e) => e.stopPropagation()}>
          <ConfirmActionModal
            title="Update grid template?"
            description={<>Replace <strong>{updateTarget.name}</strong> with the columns currently showing on the grid?</>}
            confirmButtonLabel="Update template"
            variant="default"
            onConfirm={confirmUpdate}
            onClose={() => setUpdateTarget(null)}
          />
        </div>
      )}

      {deleteTarget && (
        <div className="fixed inset-0 z-110" onClick={(e) => e.stopPropagation()}>
          <ConfirmDeleteModal
            title="Delete Grid Template?"
            confirmValue={deleteTarget.name}
            confirmLabel="template name"
            description={<>Are you sure you want to delete the grid template <strong>{deleteTarget.name}</strong>? This action cannot be undone.</>}
            deleteButtonLabel="Delete Template"
            onConfirm={confirmDelete}
            onClose={() => setDeleteTarget(null)}
          />
        </div>
      )}
    </div>
  );
}

function TemplateSection({ label, templates, isAdmin, currentUserId, onApply, onDelete, onToggleShare, onSetDefault, onUpdate, forceOwner, canCreateTemplates, knownColumnIds }: {
  label: string; templates: GridTemplate[]; isAdmin: boolean; currentUserId: string;
  onApply: (tpl: GridTemplate) => void; onDelete: (id: string) => void;
  onToggleShare: (id: string) => void; onSetDefault: (id: string) => void; onUpdate: (id: string) => void;
  forceOwner?: boolean; canCreateTemplates?: boolean;
  knownColumnIds?: string[];
}) {
  return (
    <div>
      <div className="px-5 py-2 bg-gray-50/50 border-b border-gray-100">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">{label}</p>
      </div>
      {templates.length === 0 ? (
        <p className="text-xs text-gray-400 text-center py-4">No {label.toLowerCase()}</p>
      ) : templates.map((tpl) => (
        <TemplateRow key={tpl._id} tpl={tpl} isAdmin={isAdmin} isOwner={forceOwner || tpl.createdBy.userId === currentUserId}
          onApply={onApply} onDelete={onDelete} onToggleShare={onToggleShare} onSetDefault={onSetDefault} onUpdate={onUpdate} canCreateTemplates={canCreateTemplates} knownColumnIds={knownColumnIds} />
      ))}
    </div>
  );
}

function TemplateRow({ tpl, isAdmin, isOwner, onApply, onDelete, onToggleShare, onSetDefault, onUpdate, canCreateTemplates, knownColumnIds = [] }: {
  tpl: GridTemplate; isAdmin: boolean; isOwner: boolean;
  onApply: (tpl: GridTemplate) => void; onDelete: (id: string) => void;
  onToggleShare: (id: string) => void; onSetDefault: (id: string) => void; onUpdate: (id: string) => void;
  canCreateTemplates?: boolean;
  knownColumnIds?: string[];
}) {
  const canDelete = isAdmin || (isOwner && tpl.scope === 'personal' && canCreateTemplates);
  const canUpdate = tpl.scope !== 'system' && (isAdmin || (isOwner && canCreateTemplates));
  const canShare = isAdmin && tpl.scope !== 'system';
  const columnSummary = formatTemplateColumnSummary(tpl.state, knownColumnIds);
  return (
    <div className="flex items-center gap-3 px-5 py-2.5 border-b border-gray-100 hover:bg-gray-50 transition-colors">
      <button onClick={() => onSetDefault(tpl._id)}
        className="p-1 rounded-sm transition-colors hover:bg-yellow-50 shrink-0"
        title={tpl.isDefault ? 'Remove as default' : 'Set as default for later visits (does not apply now)'}>
        <Star className={`h-4 w-4 transition-colors ${tpl.isDefault ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300 hover:text-yellow-400'}`} />
      </button>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-800 truncate">{tpl.name}</p>
        <p className="text-[10px] text-gray-400 mt-0.5">
          {columnSummary}
          {tpl.description ? ` — ${tpl.description}` : ''}
        </p>
      </div>
      <div className="flex items-center gap-0.5 shrink-0">
        <button
          type="button"
          onClick={() => onApply(tpl)}
          className="px-2 py-1 text-[11px] font-medium rounded-md text-orange-700 hover:bg-orange-50 transition-colors"
          title="Apply this layout now">
          Apply
        </button>
        {canUpdate && (
          <button
            type="button"
            onClick={() => onUpdate(tpl._id)}
            className="px-2 py-1 text-[11px] font-medium rounded-md text-gray-600 hover:bg-gray-100 transition-colors"
            title="Replace this template with the columns currently showing">
            Update
          </button>
        )}
        {canShare && (
          <button onClick={() => onToggleShare(tpl._id)}
            className={`p-1.5 rounded-sm transition-colors ${tpl.scope === 'shared' ? 'text-orange-500 hover:text-orange-600' : 'text-gray-300 hover:text-orange-500'}`}
            title={tpl.scope === 'shared' ? 'Unshare' : 'Share with everyone'}><Share2 className="h-4 w-4" /></button>
        )}
        {canDelete && (
          <button onClick={() => onDelete(tpl._id)}
            className="p-1.5 text-gray-300 hover:text-red-500 rounded-sm transition-colors" title="Delete"><Trash2 className="h-4 w-4" /></button>
        )}
      </div>
    </div>
  );
}
